import React, { useEffect, useState } from 'react';
import { Card, CardContent, CircularProgress, Grid, Typography, List, Stack, IconButton, Button, Chip, Tooltip } from "@mui/material";
import { Search, SearchIconWrapper, StyledInputBase } from "app/shared/SearchGlobal/style";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import StoreTwoToneIcon from '@mui/icons-material/StoreTwoTone';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import LanIcon from '@mui/icons-material/Lan';
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { LoadingButton } from '@mui/lab';
import CustomPagination from 'app/widgets/Shared/Pagination/CustomPagination';
import PrinterEditModal from './PrinterEditModal';
import PrintersHelpDialog from './PrintersHelpDialog';
import printerServices from 'app/services/printer-services';

// embedded: se renderiza dentro del tab Impresoras de Configuración, donde el
// título de la sección ya lo pone el propio tab.
const PrintersIndex = ({ embedded = false }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsList, setItemsList] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [listLoading, setListLoading] = useState(false);
    const [currentRecordId, setCurrentRecordId] = useState(null);
    const [openRecordEditModal, setOpenRecordEditModal] = useState(false);
    const [recordInActivationToggle, setRecordInActivationToggle] = useState(null);
    const [openHelpDialog, setOpenHelpDialog] = useState(false);

    useEffect(() => {
        if (listLoading) {
            loadItems();
        }
    }, [listLoading]);

    useEffect(() => {
        setListLoading(true);
    }, []);

    const buildItemsParams = () => {
        let par = [];
        if (searchQuery) {
            par.push(encodeURI(`q[name_or_ip_cont]=${searchQuery}`));
        }
        par.push(`page=${currentPage}`);
        par.push(`include=centre`);
        par.push(`${encodeURIComponent('q[s]')}=${encodeURIComponent('name asc')}`);
        return `?${_.join(par, '&')}`;
    };

    const loadItems = () => {
        printerServices.getPrinters(buildItemsParams())
            .then(resp => {
                setItemsList(resp.data);
                setTotalPages(resp.meta.total_pages);
            })
            .catch(() => {})
            .finally(() => setListLoading(false));
    };

    const handleSearchQueryChange = (newVal) => {
        setCurrentPage(1);
        setSearchQuery(newVal);
        setListLoading(true);
    };

    const handleRecordChange = (rec) => {
        setItemsList(_.map(itemsList, (u) => u.id == rec.id ? rec : u));
    };

    const handleRecordAdd = (rec) => {
        setItemsList(_.concat(itemsList, rec));
    };

    const togglePrinterActivation = (record) => {
        if (!record?.id) return;
        setRecordInActivationToggle(record.id);
        printerServices.togglePrinterActivation(record.id, '?include=centre')
            .then(resp => handleRecordChange(resp))
            .catch(() => {})
            .finally(() => setRecordInActivationToggle(null));
    };

    const debounceSearchQueryChange = _.debounce(handleSearchQueryChange, 450);

    const renderItemsList = () => {
        if (!itemsList?.length) {
            return <Typography>No se encontraron impresoras</Typography>;
        }

        return (
            <React.Fragment>
                <List disablePadding>
                    {_.map(itemsList, (u) => (
                        <React.Fragment key={u.id}>
                            <Card sx={{ mb: 2 }}>
                                <CardContent sx={{ display: 'flex' }}>
                                    <Grid container spacing={5}>
                                        <Grid item xs={12} md={5}>
                                            <Div sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <PrintOutlinedIcon fontSize="small" color="primary" />
                                                <Typography variant="h4" color="primary" ml={1}>{u.name}</Typography>
                                                <Chip
                                                    size="small"
                                                    color={u.active === false ? "default" : "success"}
                                                    label={u.active === false ? "Inactiva" : "Activa"}
                                                    sx={{ ml: 1 }}
                                                />
                                            </Div>
                                            <Div sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                                                <StoreTwoToneIcon fontSize="small" color="primary" />
                                                <Typography variant="body2" color="text.secondary" ml={1}>{u.centre?.code}{u.centre?.name && ` - ${u.centre?.name}`}</Typography>
                                            </Div>
                                            <Div sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                                                <LanIcon fontSize="small" color="primary" />
                                                <Typography variant="body2" color="text.secondary" ml={1}>{u.ip}:{u.port}</Typography>
                                            </Div>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Typography variant="overline">Tipo</Typography>
                                            <Typography variant="body2">{u.kind}</Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
                                                <LoadingButton
                                                    loading={recordInActivationToggle === u.id}
                                                    variant="contained"
                                                    size="small"
                                                    color={u.active === false ? "success" : "warning"}
                                                    sx={{ minWidth: 130 }}
                                                    onClick={() => togglePrinterActivation(u)}>
                                                    {u.active === false ? "Activar" : "Desactivar"}
                                                </LoadingButton>
                                                <IconButton onClick={() => { setCurrentRecordId(u.id); setOpenRecordEditModal(true); }}>
                                                    <EditOutlinedIcon />
                                                </IconButton>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </React.Fragment>
                    ))}
                </List>
            </React.Fragment>
        );
    };

    return (
        <div>
            <PrinterEditModal
                recordID={currentRecordId}
                isOpen={openRecordEditModal}
                onClose={() => setOpenRecordEditModal(false)}
                onRecordChange={handleRecordChange}
                onRecordAdd={handleRecordAdd} />
            <PrintersHelpDialog
                isOpen={openHelpDialog}
                onClose={() => setOpenHelpDialog(false)} />
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={3}
                sx={{ mb: 2 }}>
                {embedded ? <span /> : <Typography variant="h2">Impresoras</Typography>}
                <Stack direction="row" alignItems="center" spacing={3}>
                    <Tooltip arrow title="Cómo configurar una impresora">
                        <IconButton color="primary" onClick={() => setOpenHelpDialog(true)}>
                            <HelpOutlineOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        size="small"
                        color="success"
                        sx={{ width: 100 }}
                        startIcon={<ControlPointOutlinedIcon />}
                        onClick={() => { setCurrentRecordId(null); setOpenRecordEditModal(true); }}>
                        Crear
                    </Button>
                    <Search sx={{ maxWidth: { sm: 200, md: 350 } }}>
                        <SearchIconWrapper>
                            <SearchIcon />
                        </SearchIconWrapper>
                        <StyledInputBase
                            placeholder="Buscar"
                            inputProps={{ 'aria-label': 'search' }}
                            sx={{ background: 'white' }}
                            onChange={(e) => debounceSearchQueryChange(e.target.value)}
                            onKeyDown={(e) => { if (e.key == 'Enter') handleSearchQueryChange(e.target.value); }}
                        />
                    </Search>
                </Stack>
            </Stack>

            <Div sx={{ display: listLoading ? 'block' : 'none', textAlign: 'center', my: 5 }}>
                <CircularProgress color="warning" />
            </Div>

            <Grid container spacing={3.75}>
                <Grid item xs={12}>
                    {!listLoading && renderItemsList()}
                </Grid>
            </Grid>

            <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageSelect={(page) => { setCurrentPage(page); setListLoading(true); }} />
        </div>
    );
};

export default PrintersIndex;
